import { array } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { get, isEmpty, negate } from 'lodash/fp';
import { createSelector } from 'reselect';

import { decodeBase64Url } from '@mews/framework';
import { createPropsExtender } from '@mews/framework/redux';

import { parentSelector } from '../../selectors/parentSelector';
import { ApplicationState } from '../../types';
import { App } from './types';

const getApp = createSelector<ApplicationState, ApplicationState, App>(parentSelector, get('app'));

export const getSessionId = flow(getApp, get('sessionId'));
export const getAvailabilityBlockIdFromQuery = flow(getApp, get('availabilityBlockIdFromQuery'));
export const getLanguageCode = flow(getApp, get('languageCode'));
export const getClientApiLanguageCode = flow(getApp, get('clientApiLanguageCode'));
export const getLanguageCodeFromQuery = flow(getApp, get('languageCodeFromQuery'));
export const getCurrencyCode = flow(getApp, get('currencyCode'));
export const getCurrencyCodeFromQuery = flow(getApp, get('currencyCodeFromQuery'));
export const getCurrentRoute = flow(getApp, get('currentRoute'));
export const getRouteFromQuery = flow(getApp, get('routeFromQuery'));
export const getRoomFromQuery = flow(getApp, get('roomFromQuery'));
export const getHotelFromQuery = flow(getApp, get('hotelFromQuery'));
export const getAdultCountFromQuery = flow(getApp, get('adultCountFromQuery'));
export const getChildCountFromQuery = flow(getApp, get('childCountFromQuery'));
export const getCityIdFromQuery = flow(getApp, get('cityIdFromQuery'));
export const getStartDateFromQuery = flow(getApp, get('startDateFromQuery'));
export const getEndDateFromQuery = flow(getApp, get('endDateFromQuery'));
export const getVoucherCodeFromQuery = flow(getApp, get('voucherCodeFromQuery'));
export const getReservationGroupIdFromQuery = flow(getApp, get('reservationGroupIdFromQuery'));
export const getReservationIdsFromQuery = flow(getApp, get('reservationIdsFromQuery'));
export const getCategorySortingQuery = flow(getApp, get('categorySortingQuery'));
export const isFlexibleRatesFilterEnabledQuery = flow(getApp, get('flexibleRatesFilterFromQuery'));
export const getPropertyReturnUrlFromQuery = flow(
    getApp,
    get<App, 'propertyReturnUrlFromQuery'>('propertyReturnUrlFromQuery'),
    decodeBase64Url
);

export const hasPropertyUrlFromQuery = flow(getPropertyReturnUrlFromQuery, negate(isEmpty));
export const getPaymentCardIdFromQuery = flow(getApp, get('paymentCardIdFromQuery'));

export const queryPaymentCardIdPropsExtender = createPropsExtender('paymentCardId', getPaymentCardIdFromQuery);

export const getPreviousRoute = flow(getApp, get('previousRoute'));
export const isAppInitialized = flow(getApp, get('isInitialized'), Boolean);
export const hasAppInitializationFailed = flow(getApp, get('hasFailed'), Boolean);
export const isAppOpened = flow(getApp, get('isOpened'), Boolean);
export const getSelectedCityId = flow(getApp, get('selectedCityId'));
export const getAnotherHotelId = flow(getApp, get('anotherHotelId'));
export const hasAnotherHotelSelected = flow(getApp, get('anotherHotelId'), Boolean);
export const isInvalidIframe = createSelector(getApp, (app) => app.isInvalidIframe);
export const isCurrencyChangeInProgress = flow(getApp, get('isCurrencyChangeInProgress'), Boolean);

export const isAppFetching = createSelector(
    isAppInitialized,
    hasAppInitializationFailed,
    (isInitialized, hasFailed) => !(isInitialized || hasFailed)
);
export const areDeeplinkDatesSet = createSelector(getStartDateFromQuery, getEndDateFromQuery, (startDate, endDate) =>
    pipe([startDate, endDate], array.some(Boolean))
);

export const getIsFxMulticurrencyServiceEnabled = flow(getApp, get('isFxMulticurrencyServiceEnabled'), Boolean);
